import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, BedDouble, Ruler, MapPin, Video } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { ViewingModal } from "@/components/ViewingModal";

const inputCls =
  "rounded-xl bg-ink border border-white/10 px-4 py-3 text-sm text-[#F8F6F0] placeholder:text-zinc-600 focus:outline-none focus:border-gold/60 transition-colors";

export default function Listings() {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [community, setCommunity] = useState("all");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [beds, setBeds] = useState("all");
  const [maxPrice, setMaxPrice] = useState("");
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    api.get("/listings").then((r) => setListings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const communities = useMemo(() => [...new Set(listings.map((l) => l.community))].sort(), [listings]);

  const filtered = listings.filter((l) => {
    if (q && !`${l.title} ${l.community}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (community !== "all" && l.community !== community) return false;
    if (type !== "all" && l.type !== type) return false;
    if (status !== "all" && l.status !== status) return false;
    if (beds !== "all" && String(l.beds) !== beds) return false;
    if (maxPrice && l.price_aed > Number(maxPrice)) return false;
    return true;
  });

  return (
    <main className="bg-ink min-h-screen" data-testid="listings-page">
      <Seo title="Dubai Property Listings — Haven & Hill Properties"
           description="Browse curated Dubai apartments, villas and penthouses. Filter by community, type and budget, then book a live virtual viewing."
           path="/listings" />
      <Navbar />
      <div className="pt-32 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.35em] text-gold-light/90 font-medium mb-5">The collection</p>
        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F8F6F0] leading-tight max-w-2xl">
          Curated homes &amp; investments, <span className="italic gold-text">viewable from anywhere.</span>
        </h1>

        <div className="mt-10 gold-ring rounded-2xl bg-ink-card/70 p-4 flex flex-wrap gap-3" data-testid="listings-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or community…"
                   className={`${inputCls} w-full pl-10`} data-testid="listings-search-input" />
          </div>
          <select value={community} onChange={(e) => setCommunity(e.target.value)} className={inputCls} data-testid="listings-community-filter">
            <option value="all">All communities</option>
            {communities.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls} data-testid="listings-type-filter">
            <option value="all">All types</option>
            <option value="apartment">Apartment</option>
            <option value="villa">Villa</option>
            <option value="townhouse">Townhouse</option>
            <option value="penthouse">Penthouse</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls} data-testid="listings-status-filter">
            <option value="all">Ready + off-plan</option>
            <option value="ready">Ready</option>
            <option value="off-plan">Off-plan</option>
          </select>
          <select value={beds} onChange={(e) => setBeds(e.target.value)} className={inputCls} data-testid="listings-beds-filter">
            <option value="all">Any beds</option>
            {[0,1,2,3,4,5].map((b) => <option key={b} value={b}>{b === 0 ? "Studio" : `${b} BR`}</option>)}
          </select>
          <input value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} type="number" min="0" placeholder="Max budget (AED)"
                 className={`${inputCls} w-44`} data-testid="listings-price-filter" />
        </div>

        <p className="mt-6 text-xs text-zinc-500" data-testid="listings-count">{filtered.length} {filtered.length === 1 ? "property" : "properties"}</p>

        {loading ? (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} className="rounded-2xl bg-ink-card/60 animate-pulse aspect-[4/5]" />)}
          </div>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="listings-grid">
            {filtered.map((l, i) => (
              <motion.article
                key={l.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.05, 0.4), duration: 0.55 }}
                className="gold-ring rounded-2xl overflow-hidden bg-ink-card group flex flex-col"
                data-testid={`listing-card-${l.id}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={l.image_url} alt={`${l.title}, ${l.community}`} loading="lazy"
                       className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-transparent" />
                  <span className={`absolute top-4 left-4 rounded-full px-3 py-1 text-[10px] uppercase tracking-wider font-semibold ${
                    l.status === "ready" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "glass text-gold-pale border border-gold/25"
                  }`}>{l.status === "ready" ? "Ready" : "Off-plan"}</span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h2 className="font-serif text-xl text-[#F8F6F0] leading-snug">{l.title}</h2>
                  <p className="mt-1.5 text-xs text-zinc-400 flex items-center gap-1.5"><MapPin size={12} className="text-gold/80" /> {l.community}</p>
                  <div className="mt-3 flex items-center gap-4 text-xs text-zinc-400">
                    <span className="flex items-center gap-1.5"><BedDouble size={13} className="text-gold/80" /> {l.beds === 0 ? "Studio" : `${l.beds} BR`}</span>
                    <span className="flex items-center gap-1.5"><Ruler size={13} className="text-gold/80" /> {l.area_sqft} sqft</span>
                    <span className="capitalize">{l.type}</span>
                  </div>
                  <p className="mt-4 font-serif text-lg gold-text">{l.price_label}</p>
                  <p className="mt-2 text-xs text-zinc-400 leading-relaxed flex-1">{l.description}</p>
                  <div className="mt-5 flex gap-2.5">
                    <Link
                      to={`/listings/${l.id}`}
                      data-testid={`listing-details-link-${l.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-semibold text-ink hover:bg-gold-hover transition-colors"
                    >
                      View details
                    </Link>
                    <button
                      data-testid={`listing-viewing-btn-${l.id}`}
                      onClick={() => setViewing(l)}
                      className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 text-xs font-semibold text-gold-pale hover:bg-gold/10 transition-colors"
                    >
                      <Video size={14} /> Virtual viewing
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
      <Footer />
      {viewing && <ViewingModal listing={viewing} onClose={() => setViewing(null)} />}
    </main>
  );
}
